import { TranslateService } from '@ngx-translate/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges
} from '@angular/core';
import { CorrespondenceDto } from '@shared/models/correspondence-dto';
import { AppState } from '../../../core/reducers';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import {
    selectConfigurationRequestsMaxFileSize,
    selectConfigurationRequestsMaxFileSizeAllDocuments
} from '../../../core/selectors/config.selectors';
import { downloadLocalFile, downloadRemoteFile } from '../../../core/actions/application.actions';
import { AttachmentValidator } from '@shared/validators/attachment.validator';

@Component({
    standalone: false,
    selector: 'app-sgp-quill-editor',
    templateUrl: './quill-editor.component.html'
})
export class QuillEditorComponent implements OnInit, OnChanges {

    form: FormGroup;

    /**
     * Type can be 'confirmatory' or 'communication' based on usage
     */
    @Input()
    isOnCommunication: boolean = true;

    /**
     * The correspondence message
     */
    @Input()
    message: CorrespondenceDto = null;

    /**
     * The time the content of editor saved as draft
     */
    @Input()
    savedAsDraftTime: Date = null;

    /**
     * The confirmatory request message
     */
    @Input()
    content: string = '';

    /**
     * Indicates whether confirmatory request message is draft
     */
    @Input()
    isRequestMessageDraft: boolean = false;

    @Output()
    save = new EventEmitter<CorrespondenceDto>();

    @Output()
    discard = new EventEmitter<CorrespondenceDto>();

    @Output()
    send = new EventEmitter<CorrespondenceDto>();

    /**
     * On correspondence content change
     */
    @Output()
    correspondenceChange = new EventEmitter<CorrespondenceDto>();

    /**
     * On confirmatory request content change
     */
    @Output()
    contentChange = new EventEmitter<CorrespondenceDto>();

    @Output()
    keyUp = new EventEmitter<CorrespondenceDto>();

    isClickInsideReplyBox: boolean;
    isFullScreen: boolean = false;

    requestsMaxFileSize$: Observable<number>;
    requestsMaxFileSizeAllDocuments$: Observable<number>;

    placeholder: string;

    quillEditor;

    constructor(private store: Store<AppState>,
                private fb: FormBuilder,
                private attachmentValidator: AttachmentValidator,
                private translateService: TranslateService) { }

    ngOnInit() {
        this.requestsMaxFileSize$ = this.store.select(selectConfigurationRequestsMaxFileSize);
        this.requestsMaxFileSizeAllDocuments$ = this.store.select(selectConfigurationRequestsMaxFileSizeAllDocuments);

        // Editor in confirmatory
        if (!this.isOnCommunication) {
            this.placeholder = 'easeportal.editor.confirmatory.PLACEHOLDER';
        } else { // Editor in correspondence
            if (this.message) {
                this.content = this.message.content;
            }
        }

        this.form = this.fb.group({
            content: [this.message?.content, [Validators.required]],
            attachments: this.fb.array([]),
        });

        this.form.valueChanges.subscribe(v => this.contentChange.emit(v))
    }

    ngOnChanges(changes: SimpleChanges): void {
        if(!changes.message || changes.message.isFirstChange()) {
            return;
        }

        this.attachmentsArrayControl.clear();

        this.form.patchValue({
            content: this.message?.content
        });

        for(let i = 0; i < this.message.attachments.length; i++) {
            this.attachmentsArrayControl.push(new FormControl({
                documentId: this.message.attachments[i].documentId,
                name: this.message.attachments[i].title,
                size: this.message.attachments[i].sizeInBytes
            }));
        }
    }

    onEditorCreated(event): void {
        this.quillEditor = event;
    }

    sendAnswer() {
        this.send.emit(this.form.value);
    }

    saveAsDraftMessage() {
        this.save.emit(this.form.value);
    }

    discardDraft() {
        this.attachmentsArrayControl.clear();
        this.form.reset();

        // Trigger a discard event only if the Request Message is Draft.
        if (this.isRequestMessageDraft) {
            this.discard.emit(this.message);
        }
    }

    undoChanges() {
        this.quillEditor.history.undo();
    }

    redoChanges() {
        this.quillEditor.history.redo();
    }

    /**
     * On editor initialiasation this method is triggered, so we
     * check if initial content is equal with the this event content
     * If true we dont emit the event
     * @param event the content of editor
     */
    onChangeMessage(event) {
        if(event.source !== 'user') {
            return;
        }

        if (this.message) {
            this.message.content = event.html;
            this.correspondenceChange.emit(this.message);
        }
    }

    clickInsideReplyBox() {
        this.isClickInsideReplyBox = true;
        if (!this.isOnCommunication) {
            if (!this.message.content) {
                this.translateService.get('easeportal.editor.confirmatory.PREFILLED-TEXT').subscribe(template => {
                    this.message.content = template;
                });
            }
        }
    }

    clickOutsideReplyBox() {
        this.isClickInsideReplyBox = false;
    }

    onFilesSelected($event) {
        const attachments = $event.target?.files as File[];

        for(let i = 0; i < attachments.length; i++) {
            this.attachmentsArrayControl.push(new FormControl(attachments[i],
                {
                    validators: [],
                    asyncValidators: [this.attachmentValidator.createValidator()]
                }
            ));
        }
    }

    downloadFile(file) {
        if(file instanceof File) {
            this.store.dispatch(downloadLocalFile({ file }));
        } else {
            this.store.dispatch(downloadRemoteFile({ documentId: file.documentId }));
        }
    }

    deleteFile(i) {
        this.attachmentsArrayControl.removeAt(i);
    }

    _keyUp() {
        if(this.form.valid) {
            this.keyUp.emit(this.form.value);
        }
    }

    get attachmentsArrayControl(): FormArray {
        return this.form.get('attachments') as FormArray;
    }
}
